import { Approval, useIntl } from '@capsicum/common';
import { BorderedSection, CopyToClipboard, Image, InfoCard, InfoTooltip, Text } from '@capsicum/common-web';
import { type ApproveTransactionModalProps, useApproveTransactionModal } from '@capsicum/socios-core';
import { useNCWBalance } from '@capsicum/socios-core';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';

import { NetworkAndNetworkFeeCard } from '../../ui/NetworkAndNetworkFeeCard';

export const ApproveTransactionModal = ({ origin, payload }: ApproveTransactionModalProps) => {
	const { $t } = useIntl();
	const { formatCHZtoFiat } = useNCWBalance();
	const { currencyNetwork, currencyNetworkImage, isLoadingCurrencyNetworks } = useApproveTransactionModal(payload);

	const { gasFeeEstimate, to, value } = payload;
	const valueGas = (gasFeeEstimate as any)?.value ? (gasFeeEstimate as any)?.value : gasFeeEstimate;
	const gasFeeFiat = valueGas && formatCHZtoFiat(valueGas);

	const SectionRow = ({ rightNode, text }: { rightNode: ReactNode; text: ReactNode | string }) => (
		<S.Row>
			<Text variant="text3">{text}</Text>
			<S.RightNodeWrapper>{rightNode}</S.RightNodeWrapper>
		</S.Row>
	);

	return (
		<S.MainWrapper>
			<S.AppIcon alt="Spending approval Logo" height={48} src={Approval} width={48} />
			<S.Title testID="approve-transaction-modal-title" variant="title5">
				{$t('ncWallet.walletConnect.approvalModal.title')}
			</S.Title>
			<S.OriginText testID="approve-transaction-modal-origin-text" variant="text2">
				{origin}
			</S.OriginText>
			<S.Title testID="approve-transaction-modal-subtitle" variant="text3">
				{$t('ncWallet.walletConnect.approvalModal.subtitle')}
			</S.Title>
			<S.BodyWrapper>
				{value && (
					<S.SpendingCap
						icon={
							currencyNetworkImage ? (
								<Image alt="img" height={24} optimized src={currencyNetworkImage} width={24} />
							) : undefined
						}
						isLoading={isLoadingCurrencyNetworks}
						isTitleBold
						mainRightText={`${value && Number(value) < 100 ? value : $t('ncWallet.walletConnect.approvalModal.cap')} ${currencyNetwork?.currency?.shortCode ?? ''}`}
						mainRightTitleSize="large"
						showDivider={false}
						testID="walletConnect-approvalModal-spendingInfo-card"
						title={$t('ncWallet.walletConnect.approvalModal.spending')}
					/>
				)}
				{valueGas && (
					<NetworkAndNetworkFeeCard
						networkAltFee={gasFeeFiat as string | undefined}
						networkDefaultFee={valueGas ? `${valueGas} ${$t('payments.label.chilizShortCode')}` : undefined}
						networkFeeTitle={$t('ncWallet.walletConnect.approvalModal.networkFee')}
						networkFeeTooltipText={$t('ncWallet.walletConnect.transactionModal.feeTooltip')}
						networkRightText={$t('info.chilizChain')}
						networkTitle={$t('ncWallet.walletConnect.approvalModal.network')}
						testID="walletConnect-approveTransaction-modal"
					/>
				)}
				{to && (
					<BorderedSection testID="walletConnect-approvalModal-smartContract-section" title="">
						<SectionRow
							rightNode={
								<CopyToClipboard
									label={$t('ncWallet.walletConnect.confirmationModal.address')}
									testID="copy-smartContract-data"
									value={to}
								/>
							}
							text={
								<S.TooltipWRow>
									{$t('ncWallet.walletConnect.approvalModal.smartContract')}
									<S.TooltipWrapper>
										<InfoTooltip testID="walletConnect-approvalModal-smartContract-tooltip">
											{$t('ncWallet.walletConnect.approvalModal.smartContractFee')}
										</InfoTooltip>
									</S.TooltipWrapper>
								</S.TooltipWRow>
							}
						/>
					</BorderedSection>
				)}
			</S.BodyWrapper>
		</S.MainWrapper>
	);
};

namespace S {
	export const MainWrapper = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	`;

	export const BodyWrapper = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 100%;
		gap: ${({ theme }) => theme.sizing.small};
	`;

	export const AppIcon = styled(Image)`
		display: block;
		margin: 0 auto ${({ theme }) => theme.sizing.xsmall};
	`;

	export const Title = styled(Text)`
		display: block;
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.xxsmall};
	`;

	export const ValueText = styled(Text)`
		display: flex;
		justify-content: center;
		text-align: center;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const GasTextBlock = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: center;
		align-items: flex-end;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const OriginText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;

	export const SecondaryText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: center;
	`;

	export const Row = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		padding: ${({ theme }) => theme.sizing.xxsmall} 0;
	`;

	export const RightNodeWrapper = styled.div`
		${({ theme }) => theme.fonts.text.text4};
		margin-left: ${({ theme }) => theme.sizing.xxxsmall};
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const TooltipWrapper = styled.div`
		position: relative;
		z-index: 13;
		text-align: left;
	`;

	export const TooltipWRow = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.sizing.xxxsmall};
		align-items: center;
	`;

	export const SpendingCap = styled(InfoCard)`
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
		border-style: ${({ theme }) => theme.borderStyles.divider.style};
		border-width: ${({ theme }) => theme.borderStyles.divider.width};
		${({ theme }) => theme.border.radius.medium};
	`;
}
